<template>
  <div :class="wrapperClasses">
    <label v-if="label" :for="id" :class="labelClasses">
      {{ label }}
      <span v-if="required" class="input-required">*</span>
    </label>

    <div :class="inputContainerClasses">
      <div v-if="$slots.prefix" class="input-prefix">
        <slot name="prefix"></slot>
      </div>

      <input
        :id="id"
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :autofocus="autofocus"
        :class="inputClasses"
        :style="inputStyles"
        @input="$emit('update:modelValue', $event.target.value)"
        @change="$emit('change', $event.target.value)"
      />

      <div v-if="showViewPasswordButton" class="input-suffix">
        <button v-if="showViewPasswordButton" type="button" class="input-view-password" @click="handleViewPassword"><IconEyeView /></button>
      </div>
    </div>

    <div v-if="showCounter" class="input-counter">{{ modelValue ? modelValue.length : 0 }}{{ maxlength ? ` / ${maxlength}` : '' }}</div>

    <div v-if="showError && errorMessage" class="input-error">
      {{ errorMessage }}
    </div>

    <div v-if="hint && !errorMessage" class="input-hint">
      {{ hint }}
    </div>
  </div>
</template>

<script>
import IconEyeView from '@/components/icons/IconEyeView.vue';
let inputIdCounter = 0;

export default {
  name: 'AppInput',

  components: {
    IconEyeView,
  },

  props: {
    // Основные пропсы
    modelValue: {
      type: [String, Number],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'password', 'email', 'number', 'tel', 'url', 'search', 'date'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    // Состояния
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    // Валидация
    maxlength: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    step: {
      type: Number,
      default: null,
    },
    // Стилизация
    size: {
      type: String,
      default: 'medium', // small, medium, large
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    variant: {
      type: String,
      default: 'default', // default, filled, outlined
      validator: (value) => ['default', 'filled', 'outlined'].includes(value),
    },
    className: {
      type: String,
      default: '',
    },
    // Дополнительные опции
    autocomplete: {
      type: String,
      default: 'off',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    // Кастомные стили
    width: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    borderColor: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'change'],

  data() {
    return {
      id: `app-input-${inputIdCounter++}`,
      isFocused: false,
      hasError: false,
    };
  },

  computed: {
    wrapperClasses() {
      return [
        'input-wrapper',
        `input-wrapper--${this.size}`,
        `input-wrapper--${this.variant}`,
        {
          'input-wrapper--disabled': this.disabled,
          'input-wrapper--focused': this.isFocused,
          'input-wrapper--error': this.showError,
          'input-wrapper--with-prefix': this.$slots.prefix,
          'input-wrapper--with-suffix': this.$slots.suffix || this.showClearButton,
          'input-wrapper--password': this.type === 'password',
        },
        this.className,
      ];
    },

    labelClasses() {
      return [
        'input-label',
        {
          'input-label--required': this.required,
          'input-label--disabled': this.disabled,
        },
      ];
    },

    inputContainerClasses() {
      return [
        'input-container',
        {
          'input-container--focused': this.isFocused,
          'input-container--error': this.showError,
          'input-container--disabled': this.disabled,
        },
      ];
    },

    inputClasses() {
      return [
        'app-input',
        {
          'app-input--has-prefix': this.$slots.prefix,
          'app-input--has-suffix': this.$slots.suffix || this.showClearButton,
        },
      ];
    },

    inputStyles() {
      const styles = {};
      if (this.width) styles.width = this.width;
      if (this.height) styles.height = this.height;
      if (this.backgroundColor) styles.backgroundColor = this.backgroundColor;
      if (this.color) styles.color = this.color;
      if (this.borderColor) styles.borderColor = this.borderColor;
      return styles;
    },

    showClearButton() {
      return this.clearable && this.modelValue && !this.disabled && this.type !== 'password'
    },

    showViewPasswordButton() {
      return this.type === 'password' && !this.disabled;
    },

    showError() {
      return !!this.errorMessage;
    },
  },

  methods: {
    handleViewPassword() {
      this.$el.querySelector('input').type = this.$el.querySelector('input').type === 'password' ? 'text' : 'password';
    },
  },
};
</script>

<style scoped>
.input-wrapper {
  display: block;
  width: 100%;
  text-align: left;
}

.input-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  color: #999;
}

.input-label--required .input-required {
  color: #dc3545;
  margin-left: 2px;
}

.input-label--disabled {
  color: #6c757d;
  cursor: not-allowed;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

/* Размеры */
.input-wrapper--small .input-container {
  font-size: 12px;
}

.input-wrapper--medium .input-container {
  font-size: 14px;
}

.input-wrapper--large .input-container {
  font-size: 16px;
}

/* Варианты */

.input-wrapper--filled .input-container {
  background-color: #f8f9fa;
}

.input-wrapper--outlined .input-container {
  background-color: transparent;
}

/* Состояния фокуса */
.input-container--focused.input-wrapper--default .input-container {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.input-container--focused.input-wrapper--filled .input-container {
  background-color: #fff;
  border-color: #007bff;
}

.input-container--focused.input-wrapper--outlined .input-container {
  border-color: #007bff;
}

/* Ошибка */
.input-container--error.input-wrapper--default .input-container {
  border-color: #dc3545;
}

.input-container--error.input-wrapper--filled .input-container {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.input-container--error.input-wrapper--outlined .input-container {
  border-color: #dc3545;
}

.input-container--disabled .input-container {
  opacity: 0.6;
  cursor: not-allowed;
}

.app-input {
  flex: 1;
  width: 100%;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: inherit;
  color: #333;
  transition: all 0.3s ease;
  border: 1px solid #fafafa;
  border-radius: 10px;
  padding: 12px 20px;
  width: 100%;
  min-height: 50px;
  box-shadow: inset 1px 1px 4px 0 rgba(18, 34, 55, 0.2);
  background: #fff;
}

.app-input--has-prefix {
  padding-left: 40px;
}

.app-input--has-suffix {
  padding-right: 40px;
}

.app-input:disabled {
  cursor: not-allowed;
  background-color: transparent;
}

.app-input::placeholder {
  color: #6c757d;
  opacity: 0.7;
}

/* Префикс и суффикс */
.input-prefix,
.input-suffix {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  height: 100%;
  color: #bbcad8;
  z-index: 1;
}

.input-view-password svg {
  width: 1em;
  height: 1em;
}

.input-view-password {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  appearance: none;
  border: 0;
  border-radius: 0;
  background-color: transparent;
  margin: 0;
  padding: 0;
  color: #bbcad8;
  font-size: 16px;
  width: 50px;
}

.input-prefix {
  left: 12px;
}

.input-suffix {
  right: 0;
}

.input-clear {
  background: none;
  border: none;
  font-size: 18px;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.input-clear:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

/* Счетчик и подсказки */
.input-counter {
  text-align: right;
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
}

.input-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.input-hint {
  color: #6c757d;
  font-size: 12px;
  margin-top: 4px;
}

/* Размеры инпутов */
.input-wrapper--small .app-input {
  padding: 8px 12px;
}

.input-wrapper--small .app-input--has-prefix {
  padding-left: 32px;
}

.input-wrapper--small .app-input--has-suffix {
  padding-right: 32px;
}

.input-wrapper--small .input-prefix,
.input-wrapper--small .input-suffix {
  padding: 0 8px;
}

.input-wrapper--large .app-input {
  padding: 16px 20px;
}

.input-wrapper--large .app-input--has-prefix {
  padding-left: 48px;
}

.input-wrapper--large .app-input--has-suffix {
  padding-right: 48px;
}

.input-wrapper--large .input-prefix,
.input-wrapper--large .input-suffix {
  padding: 0 16px;
}

.input-wrapper--password .app-input {
  padding-right: 50px;
}
</style>
